
import React, { useRef, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  Stack,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ShareIcon from "@mui/icons-material/Share";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";

import TopBar from "../globalComponents/topbar";
import Footer from "../globalComponents/footer";

const API_BASE_URL = "https://backend-global-1vq9.onrender.com/api/lessons";

export default function CertificatePage() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const certificateRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [certificateData, setCertificateData] = useState(null);

  const user = JSON.parse(localStorage.getItem("user"));
  const learnerId = user?.id || user?._id;
  const learnerName = user?.name || user?.fullName || "Student";

  // ============================
  // FETCH CERTIFICATE DATA
  // ============================
  useEffect(() => {
    if (!learnerId) {
      setError("Please log in to view your certificate");
      setLoading(false);
      return;
    }

    loadCertificateData();
  }, [lessonId, learnerId]);

  const loadCertificateData = async () => {
    try {
      setLoading(true);

      // Fetch certificate details
      const res = await fetch(`${API_BASE_URL}/certificate/${learnerId}/${lessonId}`);
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Certificate not found");
      }

      const data = await res.json();
      setCertificateData(data.data);
    } catch (err) {
      console.error("Error loading certificate:", err);
      setError(err.message || "Failed to load certificate");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- DOWNLOAD PDF -------------------- */
  const downloadAsPDF = async () => {
    try {
      if (!window.html2pdf) {
        const script = document.createElement("script");
        script.src =
          "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
        document.body.appendChild(script);
        await new Promise((res) => (script.onload = res));
      }

      window
        .html2pdf()
        .set({
          margin: 0,
          filename: `certificate-${learnerName.replace(/\s+/g, "-")}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
        })
        .from(certificateRef.current)
        .save();
    } catch (err) {
      alert("PDF generation failed. Try PNG instead.");
    }
  };

  /* -------------------- DOWNLOAD PNG -------------------- */
  const downloadAsPNG = async () => {
    try {
      if (!window.html2canvas) {
        const script = document.createElement("script");
        script.src =
          "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
        document.body.appendChild(script);
        await new Promise((res) => (script.onload = res));
      }

      const canvas = await window.html2canvas(certificateRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const link = document.createElement("a");
      link.download = `certificate-${learnerName.replace(/\s+/g, "-")}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch {
      alert("PNG generation failed.");
    }
  };

  /* -------------------- SHARE -------------------- */
  const shareCertificate = () => {
    const text = `I just completed "${certificateData?.lessonTitle}" and earned my certificate! 🎉`;
    const url = certificateData?.certificateUrl || window.location.href;

    if (navigator.share) {
      navigator.share({ 
        title: "Certificate of Achievement", 
        text, 
        url 
      }).catch(err => console.log("Share failed:", err));
    } else {
      navigator.clipboard.writeText(`${text} ${url}`);
      alert("Certificate link copied to clipboard!");
    }
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  // ============================
  // LOADING STATE
  // ============================
  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#f5f6fa" }}>
        <TopBar />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "80vh",
          }}
        >
          <CircularProgress size={60} />
        </Box>
        <Footer />
      </Box>
    );
  }

  // ============================
  // ERROR STATE
  // ============================
  if (error || !certificateData) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#f5f6fa" }}>
        <TopBar />
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || "Certificate not found"}
          </Alert>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
            >
              Go Back
            </Button>
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={() => navigate("/curriculum")}
            >
              View Lessons
            </Button>
          </Stack>
        </Container>
        <Footer />
      </Box>
    );
  }

  /* -------------------- MAIN UI -------------------- */
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <TopBar />

      {/* Congratulations Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "#fff",
          py: 6,
          textAlign: "center",
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            🎉 Congratulations! 🎉
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            You've successfully completed the course and earned your certificate!
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Paper elevation={6} sx={{ p: 4 }}>
          {/* Page Title */}
          <Typography
            variant="h4"
            align="center"
            fontWeight="bold"
            gutterBottom
          >
            Certificate of Completion
          </Typography>

          <Divider sx={{ my: 3 }} />

          {/* Certificate Details */}
          <Box sx={{ mb: 3, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              Awarded to: <strong>{learnerName}</strong>
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Course: <strong>{certificateData.lessonTitle}</strong>
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Completion: <strong>{certificateData.completionPercentage}%</strong>
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Date: <strong>{formatDate(certificateData.issuedAt)}</strong>
            </Typography>
          </Box>

          {/* Certificate Preview */}
          <Box
            ref={certificateRef}
            sx={{
              position: "relative",
              width: "1000px",
              height: "707px",
              mx: "auto",
              bgcolor: "#fff",
              overflow: "hidden",
              border: "2px solid #e0e0e0",
              borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            }}
          >
            {/* Blue Decorative Border Top Left */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: 300,
                height: 300,
                background:
                  "linear-gradient(135deg, #1e40af, #3b82f6, #60a5fa)",
                clipPath: "polygon(0 0, 100% 0, 0 100%)",
              }}
            />

            {/* Blue Decorative Border Bottom Right */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: 350,
                height: 350,
                background:
                  "linear-gradient(135deg, #1e40af, #3b82f6, #60a5fa)",
                clipPath: "polygon(100% 0, 100% 100%, 0 100%)",
              }}
            />

            {/* Certificate Content */}
            <Box
              sx={{
                position: "relative",
                zIndex: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                px: 10,
                textAlign: "center",
              }}
            >
              {/* Title */}
              <Typography
                variant="h2"
                fontFamily="serif"
                sx={{ letterSpacing: "0.1em", mb: 1 }}
              >
                CERTIFICATE
              </Typography>
              <Typography
                variant="h5"
                fontFamily="serif"
                sx={{ letterSpacing: "0.15em", mb: 4 }}
              >
                OF ACHIEVEMENT
              </Typography>

              {/* Decorative Element */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
                <Box sx={{ width: 48, height: 1, bgcolor: "grey.400" }} />
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    mx: 1,
                    borderRadius: "50%",
                    border: "2px solid",
                    borderColor: "grey.400",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      bgcolor: "grey.400",
                    }}
                  />
                </Box>
                <Box sx={{ width: 48, height: 1, bgcolor: "grey.400" }} />
              </Box>

              {/* Presented To */}
              <Typography
                variant="body2"
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: 2,
                  color: "text.secondary",
                  mb: 2,
                }}
              >
                This certificate is proudly presented to
              </Typography>

              {/* Learner Name */}
              <Typography
                variant="h3"
                sx={{
                  fontFamily: "cursive",
                  mb: 4,
                  color: "text.primary",
                }}
              >
                {learnerName}
              </Typography>

              {/* Description */}
              <Typography
                sx={{
                  maxWidth: 700,
                  mb: 6,
                  lineHeight: 1.8,
                  color: "text.secondary",
                }}
              >
                For successfully completing the course "
                <strong>{certificateData.lessonTitle}</strong>" and
                demonstrating exceptional dedication and commitment to learning.
                This achievement reflects outstanding effort and mastery of the
                subject matter.
              </Typography>

              {/* Signatures Section */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  maxWidth: 600,
                  mt: 4,
                }}
              >
                {/* Left Signature */}
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    sx={{
                      mb: 1,
                      fontSize: "1.5rem",
                      fontFamily: "cursive",
                    }}
                  >
                    Charles
                  </Typography>
                  <Box
                    sx={{
                      width: 180,
                      borderTop: "2px solid",
                      borderColor: "grey.800",
                      pt: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      Charles KB
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ textTransform: "uppercase", color: "text.secondary" }}
                    >
                      Programe Lead and Instructor
                    </Typography>
                  </Box>
                </Box>

                {/* Center Seal */}
                <Box
                  sx={{
                    width: 96,
                    height: 96,
                    borderRadius: "50%",
                    border: "4px solid",
                    borderColor: "blue.300",
                    bgcolor: "blue.50",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      border: "2px solid",
                      borderColor: "blue.400",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg
                      style={{ width: 40, height: 40, color: "#3b82f6" }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </Box>
                </Box>

                {/* Right Signature */}
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    sx={{
                      mb: 1,
                      fontSize: "1.5rem",
                      fontFamily: "cursive",
                    }}
                  >
                    N.O.M
                  </Typography>
                  <Box
                    sx={{
                      width: 180,
                      borderTop: "2px solid",
                      borderColor: "grey.800",
                      pt: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      Nelson Oginga
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ textTransform: "uppercase", color: "text.secondary" }}
                    >
                      Program Development Manager
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Date */}
              <Typography
                variant="body2"
                sx={{ mt: 4, color: "text.secondary" }}
              >
                Date: {formatDate(certificateData.issuedAt)}
              </Typography>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            sx={{ mt: 4 }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<DownloadIcon />}
              onClick={downloadAsPDF}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                px: 4,
              }}
            >
              Download PDF
            </Button>

            <Button
              variant="contained"
              size="large"
              color="info"
              startIcon={<DownloadIcon />}
              onClick={downloadAsPNG}
              sx={{ px: 4 }}
            >
              Download PNG
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<ShareIcon />}
              onClick={shareCertificate}
              sx={{ px: 4 }}
            >
              Share
            </Button>
          </Stack>

          {/* Navigation Buttons */}
          <Divider sx={{ my: 4 }} />
          
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(`/lesson/${lessonId}`)}
            >
              Back to Lesson
            </Button>

            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={() => navigate("/curriculum")}
            >
              View All Lessons
            </Button>
          </Stack>
        </Paper>
      </Container>

      <Footer />
    </Box>
  );
}
